import { useMsal } from '@azure/msal-react'
import { createContext, useContext, useMemo } from 'react'
import { b2cLoginRequest } from '../authConfig'
import { useQuery } from '@tanstack/react-query'

export interface AccessTokenContextType {
  accessToken: string | undefined
  isPending: boolean
  loginRequired: boolean
}

export const defaultAccessTokenContext: AccessTokenContextType = {
  accessToken: undefined,
  isPending: true,
  loginRequired: false,
}

export const AccessTokenContext = createContext(defaultAccessTokenContext)
export const AccessTokenProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { instance: msalInstance } = useMsal()
  const msalAccount = msalInstance.getActiveAccount()

  const accessTokenQuery = useQuery({
    enabled: !!msalAccount,
    queryKey: ['msalAccessToken'],
    queryFn: async () => {
      const tokenResponse = await msalInstance.acquireTokenSilent(
        b2cLoginRequest
      )

      if (!tokenResponse.accessToken) {
        msalInstance
          .logoutRedirect({
            postLogoutRedirectUri: '/',
            account: msalInstance.getActiveAccount(),
          })
          .catch(() => {
            throw new Error('Failed to logout')
          })
      }
      return tokenResponse.accessToken
    },
    refetchInterval: 60 * 1000,
  })

  const context = useMemo(() => {
    return {
      accessToken: accessTokenQuery.data,
      isPending: accessTokenQuery.isFetching,
      loginRequired: !msalAccount || !!accessTokenQuery.error,
    }
  }, [msalAccount, accessTokenQuery])

  return (
    <AccessTokenContext.Provider value={context}>
      {children}
    </AccessTokenContext.Provider>
  )
}
export const useAccessTokenContext = () => useContext(AccessTokenContext)
